import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MonoLabel } from '../components/Editorial';
import { useAuth } from '../context/AuthContext';

/**
 * Post-accept welcome step for newly-created guardian users.
 *
 * URL: `/welcome?athlete_id=X&athlete_first=Y&athlete_premium=0|1`
 *
 * GuardianAcceptPage routes new guardians here after accept (existing
 * users go straight to /dashboard). The page surfaces a Pro-upgrade CTA
 * when the linked athlete is on the free tier — parents are typically
 * the payers, so this is the highest-intent moment for the upsell.
 *
 * Phone collection moved off this page: the accept step now requires it,
 * so by the time we land here the parent already has a phone on file.
 * That makes this page upgrade-only (or, when the athlete is already
 * Pro, a single-tap continue-to-dashboard).
 */
export default function GuardianWelcomePage() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const athleteId       = searchParams.get('athlete_id');
  const athleteFirst    = searchParams.get('athlete_first') || '';
  const athletePremium  = searchParams.get('athlete_premium') === '1';

  // Hand the parent off to /upgrade (there is no /billing page route —
  // /billing/* is the billing service). athlete_id makes UpgradePage bill
  // THIS athlete (honored only if linked to the guardian); utm attributes
  // conversions to this entry point.
  const upgradeUrl = athleteId
    ? `/upgrade?athlete_id=${encodeURIComponent(athleteId)}&utm_source=parent_welcome`
    : '/upgrade';

  const parentFirst = user?.first_name || '';

  return (
    <div className="ed-auth">
      <div className="ed-auth__hero">
        <div>
          <MonoLabel onDark>WELCOME</MonoLabel>
          <h1 className="ed-auth__hero-headline" style={{ marginTop: 16 }}>
            {parentFirst ? <>Hi {parentFirst}.</> : <>You're in.</>}
            <br />
            <em>{athleteFirst ? `${athleteFirst}'s ready.` : 'Let\'s get started.'}</em>
          </h1>
        </div>
        <p className="ed-auth__hero-quote">
          One quick thing before we hand you the keys.
        </p>
      </div>

      <div className="ed-auth__form-wrap">
        <div className="ed-auth__form">
          <div className="auth-logo" style={{ marginBottom: 24 }}>
            <img src="/logo.png" alt="ProZpects" style={{ maxHeight: 36 }} />
          </div>

          {!athletePremium && athleteId && (
            <div
              style={{
                padding: 20,
                border: '1px solid var(--ed-color-border)',
                borderRadius: 12,
                background: 'var(--ed-color-surface, #f7f7f5)',
              }}
            >
              <MonoLabel>UPGRADE</MonoLabel>
              <h3 style={{ fontFamily: 'var(--ed-font-serif)', fontSize: 22, fontWeight: 400, letterSpacing: '-0.01em', margin: '8px 0 8px' }}>
                Get {athleteFirst || 'your athlete'} on Pro.
              </h3>
              <p style={{ fontSize: 13, color: 'var(--ed-color-muted)', lineHeight: 1.5, margin: '0 0 16px' }}>
                ProZpects Pro unlocks college matching, coach contact details, and unlimited video for {athleteFirst || 'them'} — see which colleges fit them, and why, ranked across every signal on their profile. You can manage or cancel anytime.
              </p>
              <a
                href={upgradeUrl}
                className="auth-btn"
                style={{ display: 'inline-block', textAlign: 'center', textDecoration: 'none' }}
              >
                See plans
              </a>
            </div>
          )}

          {athletePremium && (
            <div style={{ fontSize: 13, color: 'var(--ed-color-muted)' }}>
              <strong>{athleteFirst || 'Your athlete'} is already on Pro.</strong> College matching, coach contact details, and unlimited video are all unlocked.
            </div>
          )}

          <div className="auth-footer" style={{ marginTop: 32 }}>
            <Link to="/dashboard">Skip for now</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
